/**
 * The Review cockpit's header (Figma 00a, 00, 5:2): `Topbar` is the 51px band with its hairline (brand and
 * crumb at the start, the tabs after, the reviewer at the end), `BrandLink` is the ink square with "Review" and
 * where we are, and `NavTab` is one tab with its optional count (amber, as the frames paint it).
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, type LinkProps } from '@tanstack/react-router';
import { PAGE_COLUMN, RADIUS, SPACE, TEXT, WEIGHT, usePalette } from '../theme';

/** Where on the bar a screen's own part sits: after the tabs, or before the reviewer at the bar's end. */
export type BarSlot = 'screen' | 'tools';

type Hosts = { readonly screen: HTMLElement | null; readonly tools: HTMLElement | null };

const NO_HOSTS: Hosts = { screen: null, tools: null };

type HostRef = (element: HTMLElement | null) => void;

interface SlotContext {
  readonly hosts: Hosts;
  /** The bar's hosts, as stable ref callbacks (a new callback each render would be called again each render). */
  readonly refs: { readonly screen: HostRef; readonly tools: HostRef };
}

const NO_REF: HostRef = () => undefined;

const Slots = createContext<SlotContext>({ hosts: NO_HOSTS, refs: { screen: NO_REF, tools: NO_REF } });

/** The bar's per-screen slots (PRD-333, Figma 00): wraps the frame, so a screen under the bar can put its own
 * controls on the bar (`TopbarFill`) without the bar knowing which screen it is. */
export function TopbarSlots({ children }: { children: ReactNode }) {
  const [hosts, setHosts] = useState<Hosts>(NO_HOSTS);
  const screen = useCallback((element: HTMLElement | null) => setHosts((held) => ({ ...held, screen: element })), []);
  const tools = useCallback((element: HTMLElement | null) => setHosts((held) => ({ ...held, tools: element })), []);
  const value: SlotContext = { hosts, refs: { screen, tools } };
  return <Slots.Provider value={value}>{children}</Slots.Provider>;
}

/** What a screen puts on the bar, in one slot: drawn into the bar's host, and gone when the screen is. */
export function TopbarFill({ slot, children }: { slot: BarSlot; children: ReactNode }) {
  const { hosts } = useContext(Slots);
  const host = slot === 'screen' ? hosts.screen : hosts.tools;
  return host === null ? null : createPortal(children, host);
}

export function Topbar({ start, tabs, end }: { start: ReactNode; tabs: ReactNode; end: ReactNode }) {
  const p = usePalette();
  const { refs } = useContext(Slots);
  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACE.lg,
        padding: `${SPACE.snug}px ${SPACE.wide}px`,
        borderBottom: `1px solid ${p.hairline}`,
        background: p.surface,
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    >
      {start}
      <nav aria-label="Cockpit" style={{ display: 'flex', alignItems: 'center', gap: SPACE.xs }}>
        {tabs}
      </nav>
      <div ref={refs.screen} style={{ display: 'contents' }} />
      <div style={{ flex: 1 }} />
      <div ref={refs.tools} style={{ display: 'contents' }} />
      <div style={{ paddingRight: PAGE_COLUMN.toggleRoom }}>{end}</div>
    </header>
  );
}

export function BrandLink({
  to,
  search,
  crumb,
}: {
  to: LinkProps['to'];
  search?: LinkProps['search'];
  crumb: ReactNode;
}) {
  const p = usePalette();
  return (
    <Link
      to={to}
      search={search}
      style={{ display: 'flex', alignItems: 'center', gap: SPACE.snug, textDecoration: 'none', fontSize: TEXT.body }}
    >
      <span
        aria-hidden="true"
        style={{ width: PAGE_COLUMN.logo, height: PAGE_COLUMN.logo, borderRadius: RADIUS.md, background: p.inkStrong, display: 'inline-block' }}
      />
      <span style={{ color: p.ink, fontWeight: WEIGHT.strong }}>Review</span>
      <span style={{ color: p.inkMuted, fontWeight: WEIGHT.regular }}>{crumb}</span>
    </Link>
  );
}

/** One header tab: a link, or (given no `to`) the tab's name held still while its address is not known. */
export function NavTab({
  to,
  search,
  count,
  countOf,
  children,
}: {
  to?: LinkProps['to'];
  search?: LinkProps['search'];
  /** The open count the tab wears, when the contract has one. */
  count?: number;
  /** What the count counts, in words ("open questions"), for its accessible name. */
  countOf?: string;
  children: ReactNode;
}) {
  const p = usePalette();
  const base = { display: 'inline-flex', alignItems: 'center', gap: SPACE.xs, padding: `${SPACE.tight}px ${SPACE.md}px`, borderRadius: RADIUS.md, fontSize: TEXT.body, textDecoration: 'none' } as const;
  const badge =
    count !== undefined && count > 0 ? (
      <span role="status" aria-label={`${count} ${countOf ?? 'open'}`} style={{ color: p.absence, fontWeight: WEIGHT.strong }}>
        {count}
      </span>
    ) : null;
  if (to === undefined) {
    return (
      <span style={{ ...base, color: p.inkMuted }}>
        {children}
        {badge}
      </span>
    );
  }
  return (
    <Link
      to={to}
      search={search}
      style={{ ...base, color: p.inkSecondary }}
      activeProps={{ style: { ...base, color: p.inkStrong, background: p.surfaceRaised, fontWeight: WEIGHT.medium } }}
    >
      {children}
      {badge}
    </Link>
  );
}
